<template>
  <v-dialog :value="isOpen" width="600" @input="$emit('closed')">
    <v-card>
      <v-card-title class="subheading font-weight-bold px-4">
        <span>Edit Intervention</span>
        <v-spacer></v-spacer>
        <v-icon @click="$emit('closed')">close</v-icon>
      </v-card-title>

      <v-divider></v-divider>
      <v-form ref="form" @submit.prevent="doEditAid">
        <v-container class="px-4">
          <app-alert
            class="mb-3"
            type="error"
            :message="error"
            :outline="true"
            @dismissed="error = false"
          />
          <v-layout justify-space-between row wrap>
            <v-flex xs12 v-if="canEditAidStatus">
              <v-select
                label="Status"
                v-model="aid.status"
                @change="canSave = true"
                hint="Is this intervention ongoing or completed?"
                persistent-hint
                :items="statusOptions"
                :rules="[rules.isRequired]"
                :disabled="isLoading"
                required
              ></v-select>
            </v-flex>

            <v-flex xs12 sm6>
              <v-autocomplete
                label="Location"
                v-model="aid.location_id"
                @change="canSave = true"
                hint="The location where this Intervention was done"
                persistent-hint
                :items="locationOptions"
                :rules="[rules.isRequired]"
                :loading="isLoading"
                :disabled="isLoading"
                no-data-text="No locations in selected state"
                required
              ></v-autocomplete>
            </v-flex>

            <v-flex xs12 sm6>
              <v-autocomplete
                label="Event"
                type="text"
                v-model="aid.event_id"
                @input="canSave = true"
                hint="The cause of the intervention"
                persistent-hint
                :items="eventOptions"
                :loading="isLoading"
                :disabled="isLoading"
                :rules="[rules.isRequired]"
                no-data-text="No events available"
                required
              ></v-autocomplete>
            </v-flex>

            <v-flex xs12 sm6>
              <v-autocomplete
                label="Intervention Sector"
                v-model="aid.sector_id"
                @change="canSave = true"
                :items="sectorOptions"
                :loading="isLoading"
                :disabled="isLoading"
                :rules="[rules.isRequired]"
                required
              ></v-autocomplete>
            </v-flex>

            <v-flex xs12 sm6>
              <v-select
                label="Intervention Type"
                v-model="aid.category_id"
                @change="canSave = true"
                :items="aidCategories"
                :loading="isLoading"
                :disabled="isLoading"
                :rules="[rules.isRequired]"
                required
              ></v-select>
            </v-flex>

            <v-flex xs12>
              <v-textarea
                label="Intervention Description"
                type="text"
                v-model="aid.description"
                @input="canSave = true"
                hint="A description of the Intervention (Ex. Trainings on WASH)"
                persistent-hint
                :rules="[rules.isRequired]"
                rows="2"
                required
              ></v-textarea>
            </v-flex>

            <!-- Start Date Picker -->
            <v-flex xs12 sm6>
              <v-dialog
                ref="startDateDialog"
                v-model="startDateModal"
                :return-value.sync="aid.timestamp"
                persistent
                lazy
                full-width
                width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    label="Start Date"
                    v-model="aid.timestamp"
                    hint="The start date of the Intervention"
                    persistent-hint
                    :rules="[rules.isRequired]"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="aid.timestamp" scrollable>
                  <v-spacer></v-spacer>
                  <v-btn flat color="primary" @click="startDateModal = false">Cancel</v-btn>
                  <v-btn
                    flat
                    color="primary"
                    @click="updateFormattedDate('start', aid.timestamp)"
                  >OK</v-btn>
                </v-date-picker>
              </v-dialog>
            </v-flex>

            <!-- End Date Picker -->
            <v-flex xs12 sm6>
              <v-dialog
                ref="endDateDialog"
                v-model="endDateModal"
                :return-value.sync="aid.timestamp_end"
                persistent
                lazy
                full-width
                width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    label="End Date"
                    v-model="aid.timestamp_end"
                    hint="The end date of the Intervention"
                    persistent-hint
                    :rules="[rules.isRequired]"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="aid.timestamp_end" :allowed-dates="allowedDates" scrollable>
                  <v-spacer></v-spacer>
                  <v-btn flat color="primary" @click="endDateModal = false">Cancel</v-btn>
                  <v-btn
                    flat
                    color="primary"
                    @click="updateFormattedDate('end', aid.timestamp_end)"
                  >OK</v-btn>
                </v-date-picker>
              </v-dialog>
            </v-flex>

            <v-flex xs12 sm6>
              <v-autocomplete
                label="Partner"
                v-model="aid.agency_id"
                @change="canSave = true"
                hint="The implementing partner"
                persistent-hint
                :items="agencyOptions"
                :loading="isLoading"
                :disabled="isLoading"
                :rules="[rules.isRequired]"
                required
              ></v-autocomplete>
            </v-flex>

            <v-flex xs12 sm6>
              <v-text-field
                label="Intervention Sponsor"
                type="text"
                v-model="aid.sponsor"
                @input="canSave = true"
                hint="Name of Interventon Sponsor"
                persistent-hint
                :rules="[rules.isRequired]"
                required
              ></v-text-field>
            </v-flex>

            <v-flex xs12 sm6>
              <v-text-field
                label="Intervention Value (₦)"
                type="number"
                v-model="aid.value"
                @input="canSave = true"
                hint="Total cost of Intervention in Naira (₦)"
                persistent-hint
                :rules="[rules.isRequired]"
                required
              ></v-text-field>
            </v-flex>

            <v-flex xs12 sm6>
              <v-text-field
                label="Beneficiary Count"
                type="number"
                v-model.number="aid.count"
                @input="canSave = true"
                hint="An estimation of total beneficiary count"
                persistent-hint
                :rules="[rules.isRequired]"
                required
              ></v-text-field>
            </v-flex>

            <v-flex xs12 sm6>
              <v-select
                label="Post Distribution Monitoring?"
                v-model="aid.pdm"
                @input="canSave = true"
                hint="Post Distribution Monitoring?"
                persistent-hint
                :items="pdmOptions"
                :rules="[rules.isRequired]"
                required
              ></v-select>
            </v-flex>

          </v-layout>

          <v-layout class="mt-4 mb-2">
            <v-btn depressed color="accent" type="submit" :disabled="isSaving || !canSave">
              Save
              <v-progress-circular
                class="ml-3"
                :indeterminate="true"
                :size="18"
                :width="2"
                v-if="isSaving"
              ></v-progress-circular>
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn depressed @click="$emit('closed')">Cancel</v-btn>
          </v-layout>
        </v-container>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import { AID_UPDATE } from '@/store/actions/aid'
import { FETCH_CATEGORY } from '@/store/actions/category'
import { SECTOR_FETCH } from '@/store/actions/sector'
import { EVENT_FETCH } from '@/store/actions/event'
import { LOCATION_FETCH } from '@/store/actions/location'

import { progressOptions, booleanOptions } from '@/data/options'
import rolesData from '@/data/roles'

export default {
	name: 'EditAid',
  props: ['isOpen', 'aidData'],
  created () {
    this.isLoading = true
    Promise.all([
      this.$store.dispatch(FETCH_CATEGORY, { model: 'category' }),
      this.$store.dispatch(LOCATION_FETCH),
      this.$store.dispatch(SECTOR_FETCH),
      this.$store.dispatch(EVENT_FETCH)
    ]).then(() => {
      this.isLoading = false
    })
  },
	data () {
		return {
      aid: {
        _id: this.aidData._id,
        status: this.aidData.status,  
        tag: '-',    
        timestamp: (new Date(this.aidData.timestamp * 1000)).toISOString().split('T')[0],
        timestamp_end: (new Date(1559347200 * 1000)).toISOString().split('T')[0],
        value: this.aidData.value,
        count: this.aidData.count,
        description: this.aidData.description,
        sponsor: this.aidData.sponsor,
        pdm: this.aidData.pdm,
        agent_id: this.aidData.agent_id,
        sector_id: this.aidData.sector_id,
        category_id: this.aidData.category_id,
        event_id: this.aidData.event_id,
        location_id: this.aidData.location_id,
        state_id: this.aidData.state_id,
        agency_id: this.aidData.agency_id,
        country_id: this.aidData.country_id
      },
			canSave: false,
			isSaving: false,
			isLoading: false,
      statusOptions: progressOptions,
      pdmOptions: booleanOptions,
      startDateModal: false,
      endDateModal: false,
			error: false,
      rules: {
        isRequired: value => this.$validator.isRequired(value)
      }
		}
	},
	computed: {
    sectorOptions () {
      return this.$store.getters.sectorsAsOptions
    },
    aidCategories () {
      return this.$store.getters.categoriesAsOptions('aid', this.aid.sector_id)
    },
    eventOptions () {
      return this.$store.getters.eventsAsOptions('location', this.aid.location_id)
    },
    locationOptions () {
      return this.$store.getters.locationsAsOptions(this.aid.state_id)
    },
    agencyOptions () {
      let agencyOptions = this.$store.getters.agenciesAsOptions
      // If agency agent, show only agents agency in list
      if (this.currentUser.parent && this.currentUser.parent === 'agency') {
        const agency = agencyOptions.find(agencyOption => this.currentUser.parent_id === agencyOption.value)
        agencyOptions = [agency]
      }
      return agencyOptions
    },
    /**
     * Permission for who can edit the status of an aid
     */
    canEditAidStatus () {
      return rolesData.heads.includes(this.currentUser.role)
    },
    ...mapGetters(['currentUser'])
	},
	methods: {
    allowedDates (date) {
      return new Date(date) > new Date(this.aid.timestamp)
    },
    updateFormattedDate (type, date) {
      this.canSave = true
      this.$refs[`${type}DateDialog`].save(date)
    },
		doEditAid () {
      if (!this.$refs.form.validate()) return

      this.isSaving = true
      this.error = false

      const aid = Object.assign({}, this.aid)
      aid.timestamp = Math.round((new Date(this.aid.timestamp)).getTime() / 1000)
      aid.timestamp_end = Math.round((new Date(this.aid.timestamp_end)).getTime() / 1000)

      this.$store.dispatch(AID_UPDATE, aid)
        .then((aid) => {
          this.$emit('saved', aid)
          this.$emit('closed')
        })
        .catch(error => {
          this.error = error.message
        })
        .finally(() => {
          this.canSave = false
          this.isSaving = false
        })
		}
	}
}
</script>
